<template>
    <ul class="forum-post-list">
        <li :class="getLiClass(post)" :key="post.id" :id="'post_' + post.id" v-for="(post, index) in posts">
            <ForumPost :post="post" :postUrl="getPostUrl(post)" :sourcePost="getSourcePost(post)" :sourcePostLoading="isSourcePostLoading(post)" :answerPost="getAnswerPost(post)" :answerPostLoading="isAnswerPostLoading(post)" :canShare="true" :canLike="true" :canReply="true" :canEdit="thread.canEditPost(post.id)" :canDelete="thread.canDeletePost(post.id)" :canSolve="thread.canEditThread()" :isLiked="isPostLiked(post)" :isAnswer="isPostAnswer(post)" :isEditing="isEditingPost(post.id)" :isReplyingTo="hasReplyToPost(post.id)" @edit="startEditingPost(post.id)" @cancel="stopEditingPost(post.id)" @save="updatePost(post.id, $event)" @reply="startReplyToPost(post.id)" @like="likePost(post.id)" @unlike="unlikePost(post.id)" @delete="deletePost(post.id)" @solve="solveThread(post.id)" @unsolve="unsolveThread(post.id)" @go-source-post="goToPost(post.sourcePostId)" @go-answer-post="goToAnswerPost()">
                <template slot="saveEditorLabel">
                    Update
                </template>
            </ForumPost>
            <div class="forum-post-reply" v-if="hasReplyToPost(post.id)">
                <ForumPost :post="getReplyToPost(post.id)" :isEditing="true" @cancel="closeReplyToPost(post.id)" @save="saveReplyToPost(post.id, $event)">
                    <template slot="saveEditorLabel">
                        Post reply
                    </template>
                </ForumPost>
            </div>
            <ForumThreadSummary :thread="thread.thread" :canEdit="thread.canEditThread()" :isEditing="isEditingSummary" :termTypes.sync="summaryTermTypes" :pathIds.sync="summaryPathIds" :assignmentIds.sync="summaryAssignmentIds" :tagNames.sync="summaryTagNames" @edit="startEditingSummary()" @cancel="stopEditingSummary()" @save="updateThread()" @go-last-post="goToLastPost()" v-if="isBeginningOfThread && index === 0">
            </ForumThreadSummary>
        </li>
    </ul>
</template>
<script>
// ForumPostList: a continuous list of forum posts. Manages post edits and
// replies. Optionally renders the forum thread summary after the first
// post.

// Events:
//  - update-thread
//  - solve-thread
//  - unsolve-thread
//  - create-post
//  - update-post
//  - like-post
//  - unlike-post
//  - view-post
//  - delete-post
//  - go-post

import ForumPost from './post';
import ForumThreadSummary from './thread-summary';
import ForumUtils from 'src/utils/forum';
import JobUtils from 'src/utils/jobs';

/**
 * Converts a coordinate/value from one coordinate space to another.
 *
 * For example, given a value of 5, you can calculate the relative position
 * between two bounds, such as 0 and 20:
 *
 *   scaleCoord({
 *     inVal: 5,
 *     inMin: 0,
 *     inMax: 20,
 *     outMin: 0,
 *     outMax: 1
 *   }) // returns 0.25
 *
 * @param {object} params
 * @param {number} params.inVal
 * @param {number} params.inMin
 * @param {number} params.inMax
 * @param {number} params.outMin
 * @param {number} params.outMax
 */
function scaleCoord(params) {
    const outDiff = params.outMax - params.outMin;
    const inDiff = params.inMax - params.inMin;
    const inRel = (params.inVal - params.inMin) / inDiff;
    return params.outMin + inRel * outDiff;
}

function getTopPos(elem) {
    return $(elem).offset().top;
}

function getBottomPos(elem) {
    const $elem = $(elem);
    return $elem.offset().top + $elem.outerHeight();
}

export default {
    props: {
        thread: {
            type: Object, // ForumUtils.Thread
            required: true,
        },
        postStore: {
            type: Object, // ForumUtils.PostStore
            required: true,
        },
        postWindow: {
            type: Object, // ForumUtils.PostWindow
            required: true,
        },

        // User details to show when creating new replies
        currentUser: {
            type: Object, // User (see user service)
            required: false,
        },
    },

    components: {
        ForumPost,
        ForumThreadSummary,
    },

    data() {
        return {
            isEditingSummary: false,
            summaryTermTypes: [],
            summaryPathIds: [],
            summaryAssignmentIds: [],
            summaryTagNames: [],

            postEdits: {},
            postReplies: {},

            postFlashes: {},
            postFlashDuration: 2000,

            postsViewed: {
                // postId: false,
            },
            postsInView: {
                // postId: {
                //   ticks: 0,
                // }
            },
            postViewTickThreshold: 2, // # ticks before "viewed"
            postViewTickDuration: 500, // 500ms per tick
            updatePostsInView_job: new JobUtils.Job(
                this.updatePostsInView, {
                    rateLimitPeriod: 500, // Doesn't need to match tick
                    rateLimitFollowUp: true,
                    parallelRunFollowUp: true,
                },
            ),
            postViewTick_interval: null,

            // Cache the top pos of posts as we calculate them. Calling
            // jQuery's offset() uses getBoundingClientRect() internally,
            // which is an expensive operation.
            //
            // We'll have to clear this cache from time to time, such as
            // when the posts change. We could try to do so intelligently
            // (like not clearing the cache when posts were simply
            // appended), but that's an optimization for later.
            layoutCache: {
                rootTop: null,
                rootBottom: null,
                postTops: {},
            },
            postId: null,
        };
    },

    computed: {
        currentAuthor() {
            if (!this.currentUser) {
                return null;
            }
            // TODO: remove this hack and get the roles from currentUser
            // (requires changes to UserService.User)
            const fName = this.currentUser.firstName;
            const lName = this.currentUser.lastName;
            const roles = JSON.parse(localStorage.roles);
            return {
                name: `${fName} ${lName}`,
                roles: roles,
                avatarUrl: this.currentUser.avatar,
                currentTerm: this.currentUser.currentTerm,
            };
        },

        posts() {
            return this.postWindow.loadedPosts;
        },

        timelineBounds() {
            if (this.postStore.length === null) {
                return {
                    min: 0,
                    max: 0,
                };
            }
            const offset = this.postWindow.offset;
            const length = this.postWindow.length;
            const totalLength = this.postStore.length;
            return {
                min: offset / totalLength,
                max: (offset + length) / totalLength,
            };
        },

        isBeginningOfThread() {
            return this.postWindow.firstPostIndex === 0;
        },
    },

    watch: {
        posts: {
            handler() {
                this.onLayoutChange();

                this.$nextTick(()=>{
                    if (this.linkedPostId) {
                        const linkedPost = document.getElementById('post_' + this.linkedPostId)
                        if (linkedPost) {
                            linkedPost.scrollIntoView();
                        } else {
                            const len = this.posts.length;                        
                            const p = document.getElementById('post_' + len);
                            p.scrollIntoView();
                        }
                        
                    }
                })
            },
            deep: true
        },
    },

    created() {
        this.resetSummaryFields();
    },

    mounted() {
        // Not all images and videos have their dimensions saved as
        // attributes, so they might affect the layout once they load.
        this.$el.addEventListener('load', () => {
            this.$nextTick(this.onLayoutChange);
        }, {
            capture: true,
        });
        this.$el.addEventListener('loadedmetadata', () => {
            this.$nextTick(this.onLayoutChange);
        }, {
            capture: true,
        });

        this.postViewTick_interval = setInterval(this.onPostViewTick, 500);

        window.addEventListener('resize', this.onResize);
        window.addEventListener('scroll', this.onScroll);

        if (this.$route.params.postId) {
            this.linkedPostId = this.$route.params.postId;
            const postIsLoaded = document.getElementById('post_' + this.linkedPostId);
            if (postIsLoaded) {
                postIsLoaded.scrollIntoView();
            } else {
                setTimeout(() =>{
                    const lastPost = document.getElementById('post_20');
                    lastPost.scrollIntoView();
                },100)
            }
        }

    },

    beforeDestroy() {
        clearInterval(this.postViewTick_interval);

        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);

        for (const postId in this.postFlashes) {
            const flash = this.postFlashes[postId];
            clearTimeout(flash.timeout);
        }
    },

    methods: {
        onResize() {
            this.onLayoutChange();
        },

        onScroll() {
            // If the job triggers immediately, it could be fairly
            // expensive. We don't want to block in the scroll handler, so
            // we can use the setTimeout(_, 0) trick to schedule it.
            setTimeout(() => this.updatePostsInView_job.trigger(), 0);
        },

        onLayoutChange() {
            this.layoutCache = {
                rootTop: null,
                rootBottom: null,
                postTops: {},
            };
            this.updatePostsInView_job.trigger();
        },

        startEditingSummary() {
            this.resetSummaryFields();
            this.isEditingSummary = true;
        },

        stopEditingSummary() {
            this.isEditingSummary = false;
            this.resetSummaryFields();
        },

        updateThread() {
            this.$emit('update-thread', {
                id: this.thread.id,
                termTypes: this.summaryTermTypes,
                pathIds: this.summaryPathIds,
                assignmentIds: this.summaryAssignmentIds,
                tagNames: this.summaryTagNames,
            });
        },

        resetSummaryFields() {
            const thread = this.thread.thread;
            this.summaryTermTypes = thread.termTypes.slice();
            this.summaryPathIds = thread.paths.map(p => p.id);
            this.summaryAssignmentIds = thread.assignments.map(a => a.id);
            this.summaryTagNames = thread.tags.map(t => t.name);
        },

        isEditingPost(postId) {
            return !!this.postEdits[postId];
        },

        startEditingPost(postId) {
            this.$set(this.postEdits, postId, true);
        },

        stopEditingPost(postId) {
            this.$delete(this.postEdits, postId);
        },

        updatePost(postId, fields) {
            this.$emit('update-post', {
                id: postId,
                content: fields.content,
                contentFiles: fields.contentFiles,
            });
        },

        hasReplyToPost(sourcePostId) {
            return !!this.postReplies[sourcePostId];
        },

        getReplyToPost(sourcePostId) {
            return this.postReplies[sourcePostId];
        },

        startReplyToPost(sourcePostId) {
            if (!this.currentAuthor) {
                return;
            }
            if (!this.hasReplyToPost(sourcePostId)) {
                this.$set(this.postReplies, sourcePostId, {
                    sourcePostId: sourcePostId,
                    id: null,
                    author: this.currentAuthor,
                    content: '',
                    createdAt: null,
                    updatedAt: null,
                });
            }
        },

        closeReplyToPost(sourcePostId) {
            if (this.hasReplyToPost(sourcePostId)) {
                this.$delete(this.postReplies, sourcePostId);
            }
        },

        saveReplyToPost(sourcePostId, fields) {
            this.$emit('create-post', {
                sourcePostId: sourcePostId,
                content: fields.content,
                contentFiles: fields.contentFiles,
                authorId: this.currentAuthor.id,
            });
        },

        likePost(postId) {
            this.$emit('like-post', postId);
        },

        unlikePost(postId) {
            this.$emit('unlike-post', postId);
        },

        deletePost(postId) {
            this.$emit('delete-post', postId);
        },

        solveThread(postId) {
            this.$emit('solve-thread', postId);
        },

        unsolveThread(postId) {
            this.$emit('unsolve-thread', postId);
        },

        goToPost(postId) {
            this.$emit('go-post', postId);
        },

        goToAnswerPost() {
            this.goToPost(this.thread.thread.answerPost.id);
        },

        goToLastPost() {
            this.goToPost(this.thread.thread.lastPost.id);
        },

        _pageYToPostIndex(pageY) {
            // We could optimize this further by doing a binary search, but
            // for now a simple linear search will work.

            const firstPostIndex = this.postWindow.firstPostIndex;
            const lastPostIndex = this.postWindow.lastPostIndex;

            for (let pi = firstPostIndex; pi <= lastPostIndex; ++pi) {
                const postTop = this._getPostTop(pi);
                const postHeight = this._getDistanceToNextPost(pi);
                const nextPostTop = postTop + postHeight;

                if (pageY < postTop) {
                    return null;
                }
                if (pageY < nextPostTop) {
                    return pi;
                }
            }
            return null;
        },

        /**
         * Converts a pageY value (ie. an offset relative to the top of the
         * document) to the corresponding timeline value. If the pageY
         * doesn't map exactly, it will do clamp the timeline value to the
         * closest value within range.
         */
        pageYToTimelineValue(pageY) {

            // Check if pageY is within the posts element; if it's not,
            // we'll just have to return the min or max value
            if (pageY < this._getRootTop()) {
                return this.timelineBounds.min;
            }
            if (pageY >= this._getRootBottom()) {
                return this.timelineBounds.max;
            }

            // pageY is somewhere within our list, we just need to find
            // which post element it's in.
            const postIndex = this._pageYToPostIndex(pageY);

            // In the edge case that we don't find a post element, we can
            // assume that pageY is juuust past them all.
            //
            // TODO: a lot has changed since this check was added, so maybe
            // it can be removed?
            if (postIndex === null) {
                return this.timelineBounds.max;
            }

            // Now that we have a post element, we need to calculate the
            // position within that post to get an exact timeline value.
            // For example, if we have 10 posts, the second post will begin
            // at timeline value 0.1 and end at 0.2; halfway through would
            // be 0.15, etc.
            const postTopPos = this._getPostTop(postIndex);
            const postHeight = this._getDistanceToNextPost(postIndex);

            const postMinTv = postIndex / this.postStore.length;
            const postMaxTv = (postIndex + 1) / this.postStore.length;

            return scaleCoord({
                inVal: pageY,
                inMin: postTopPos,
                inMax: postTopPos + postHeight,
                outMin: postMinTv,
                outMax: postMaxTv,
            });
        },

        _timelineValueToPostIndex(timelineValue) {
            return Math.min(
                Math.floor(timelineValue * this.postStore.length),
                this.postStore.length - 1,
            );
        },

        /**
         * Converts a timeline value to the corresponding pageY value (ie.
         * the offset from the top of the document). If the timeline value
         * is out of range, it will clamp the pageY to the closest value in
         * range.
         */
        timelineValueToPageY(timelineValue) {

            // Check if timelineValue is before or after our post window.
            if (timelineValue <= this.timelineBounds.min) {
                return this._getRootTop();
            }
            if (timelineValue >= this.timelineBounds.max) {
                return this._getRootBottom();
            }

            // timelineValue is somewhere within our list, we just need to
            // find which post element it's in.
            const postIndex = this._timelineValueToPostIndex(timelineValue);

            // Now that we have a post element, we need to calculate the
            // position within that post to get an exact pageY.
            const postTopPos = this._getPostTop(postIndex);
            const postHeight = this._getDistanceToNextPost(postIndex);

            const postMinTv = postIndex / this.postStore.length;
            const postMaxTv = (postIndex + 1) / this.postStore.length;

            return scaleCoord({
                inVal: timelineValue,
                inMin: postMinTv,
                inMax: postMaxTv,
                outMin: postTopPos,
                outMax: postTopPos + postHeight,
            });
        },

        _getDistanceToNextPost(postIndex) {
            // TODO: check bounds

            const relPostIndex = postIndex - this.postWindow.offset;

            if (relPostIndex < this.postWindow.length - 1) {

                const currTop = this._getPostTop(postIndex);
                const nextTop = this._getPostTop(postIndex + 1);
                return nextTop - currTop;

            } else {
                const currTop = this._getPostTop(postIndex);
                const nextTop = this._getRootBottom();
                return nextTop - currTop;
            }
        },

        _getRootTop() {
            if (typeof this.layoutCache.rootTop !== 'number') {
                this.layoutCache.rootTop = getTopPos(this.$el);
            }
            return this.layoutCache.rootTop;
        },

        _getRootBottom() {
            if (typeof this.layoutCache.rootBottom !== 'number') {
                this.layoutCache.rootBottom = getBottomPos(this.$el);
            }
            return this.layoutCache.rootBottom;
        },

        _getPostTop(postIndex) {
            if (typeof this.layoutCache.postTops[postIndex] !== 'number') {
                const relPostIndex = postIndex - this.postWindow.offset;
                const postElem = this.$el.children[relPostIndex];
                const postTop = postElem ?
                    getTopPos(postElem) :
                    this._getRootTop();
                this.layoutCache.postTops[postIndex] = postTop;
            }
            return this.layoutCache.postTops[postIndex];
        },

        getPostUrl(post) {
            const thread = this.thread.thread;
            const slug = ForumUtils.formatTitlePreview(thread.title);
            const res = this.$router.resolve({
                name: 'forum-post',
                params: {
                    threadId: thread.id,
                    threadTitlePreview: slug,
                    postId: post.id,
                },
            });
            return `${window.location.origin}/${res.href}`;
        },

        hasSourcePost(post) {
            return !!post.sourcePostId;
        },

        hasAnswerPost(post) {
            // If the thread has been answered, only show the answer on the
            // first post
            const thread = this.thread.thread;
            if (!thread.answerPost) {
                return false;
            }
            return post.id === thread.firstPost.id;
        },

        getSourcePost(post) {
            if (this.hasSourcePost(post)) {
                return this.postStore.findPostById(post.sourcePostId);
            } else {
                return null;
            }
        },

        getAnswerPost(post) {
            if (this.hasAnswerPost(post)) {
                const thread = this.thread.thread;
                return this.postStore.findPostById(thread.answerPost.id);
            } else {
                return null;
            }
        },

        isSourcePostLoading(post) {
            return (this.hasSourcePost(post) &&
                !this.postStore.isPostByIdLoaded(post.sourcePostId));
        },

        isAnswerPostLoading(post) {
            const thread = this.thread.thread;
            return (this.hasAnswerPost(post) &&
                !this.postStore.isPostByIdLoaded(thread.answerPost.id));
        },

        isPostLiked(post) {
            return this.thread.hasLikedPost(post.id);
        },

        isPostAnswer(post) {
            return this.thread.isPostAnswer(post.id);
        },

        /**
         * Briefly highlight a post. This is used, for example, to draw
         * attention to a newly created post.
         */
        flashPost(postId) {
            // We can gracefully handle re-flashing a post (for whatever
            // reason) by looking for the existing flash and resetting the
            // timeout. If there isn't an existing flash (which will be the
            // usual case), we can just create one.
            let flash = this.postFlashes[postId];
            if (!flash) {
                flash = { timeout: null };
                this.$set(this.postFlashes, postId, flash);
            }

            // Reset the timeout to gracefully handle re-flashes
            clearTimeout(flash.timeout);

            // After the flash duration, remove the flash
            flash.timeout = setTimeout(() => {
                this.$delete(this.postFlashes, postId);
            }, this.postFlashDuration);
        },

        getLiClass(post) {
            return {
                flash: !!this.postFlashes[post.id],
            };
        },

        updatePostsInView() {
            const vy1 = window.scrollY;
            const vy2 = vy1 + window.innerHeight;
            if (vy1 > this._getRootBottom() || vy2 < this._getRootTop()) {
                return;
            }

            let pi1 = this.postWindow.firstPostIndex;
            let pi2 = this.postWindow.lastPostIndex;
            if (vy1 >= this._getRootTop()) {
                pi1 = this._pageYToPostIndex(vy1);
            }
            if (vy2 <= this._getRootBottom()) {
                pi2 = this._pageYToPostIndex(vy2);
            }

            // Remove posts that aren't in view anymore
            for (const pi of Object.keys(this.postsInView)) {
                if (pi < pi1 || pi > pi2) {
                    delete this.postsInView[pi];
                }
            }

            // Add posts that are in view
            for (let pi = pi1; pi <= pi2; ++pi) {
                if (this.postsViewed[pi]) {
                    continue;
                }
                if (!this.postsInView[pi]) {
                    this.postsInView[pi] = {
                        ticks: 0,
                    };
                }
            }
        },

        onPostViewTick() {
            const ticksPerView = this.postViewTickThreshold;

            for (const pi of Object.keys(this.postsInView)) {
                this.postsInView[pi].ticks += 1;

                if (this.postsInView[pi].ticks >= ticksPerView) {
                    this.postsViewed[pi] = true;
                    this.$emit('view-post', pi);
                    delete this.postsInView[pi];
                }
            }
        },
    },
}

</script>
<style lang="scss" scoped>
ul.forum-post-list {
    margin: 0;
    padding: 0;

    // Extra specificity needed in selector for this
    border-bottom: 1px solid #333;
}

li {
    list-style: none;
    background: transparent;
    transition: background 1s ease-out;
    position: relative;

    // Extend the background flash so it's not tight against the avatar.
    &::before {
        content: '';
        background: inherit;
        position: absolute;
        top: 0;
        left: -12px;
        right: 100%;
        bottom: 0;
    }

    // Extend the background flash to cover any margin between posts.
    &::after {
        content: '';
        display: table;
    }

    &.flash {
        background: #333;
        transition: none;
    }

    .forum-post {
        background: none;
        border-top: 1px solid #333;
        margin: 0;
        padding-bottom: 20px;
    }

    .forum-post-reply {
        margin-left: 62px;
        margin-bottom: 40px;
    }
}

.forum-thread-summary {
    margin-left: 62px;
    margin-bottom: 20px;
}

</style>



// WEBPACK FOOTER //
// post-list.vue?6de5d5c3